/**
 * The one ward-list display decision that is not about the patient.
 *
 * Everything else that was here — the name, the identifier, the age, the safety
 * badges — moved to `patient/patientDisplay.ts` when patient search arrived
 * (PHASE-0.md §2.3), because a search hit and a ward-list row are the same patient
 * seen from two screens and a second copy of `safetyBadges` would be a second place
 * for the allergy precedence to be decided. What stays is what is about the ward list
 * itself: the responsible physician, which is a property of the admission.
 *
 * **`t` is a parameter, not an import** — see `patient/patientDisplay.ts` for why.
 */

import type { TFunction } from 'i18next'

import type { WardListEntry } from './wardApi'

/**
 * The lookup the ward list needs: `common` for the shared values, `ward` for its own.
 *
 * `common` comes **first** deliberately. It makes this assignable to
 * `patient/patientDisplay.ts`'s `Translate`, so the same `t` renders both a column
 * heading and a safety badge — see that module's remarks. Every key below names its
 * namespace anyway; a bare key that resolves by position is a key that moves when
 * somebody adds a namespace.
 */
export type WardTranslate = TFunction<['common', 'ward']>

/**
 * The responsible doctor, or a statement that the record does not document one.
 *
 * **Its own message rather than the shared `value.notRecorded`**, for two reasons
 * that both point the same way. Today this column is empty on every bed — no
 * fixture encounter carries a `participant` and `BundleMapper` returns null — so
 * this is the string a clinician actually reads at the §8 sessions, and
 * *Ej dokumenterad* says what is true of the record where *Ej angivet* sounds like a
 * field somebody left blank. The other reason is grammatical and would apply even
 * once the column fills: Swedish adjectives agree with the noun, and `läkare` is an
 * en-word, so the one shared *Ej angivet* cannot be corrected to *Ej dokumenterad*
 * without disagreeing with `personnummer` in the next column. One string per column
 * is what lets each one be right.
 *
 * See docs/STATUS.md — the column itself, the fixture and the importer are
 * deliberately left alone; the fixture holds reviewed clinical content.
 */
export function responsibleDoctor(entry: WardListEntry, t: WardTranslate): string {
  const family = entry.responsibleFamilyName ?? ''
  const given = entry.responsibleGivenName ?? ''

  if (family === '' && given === '') {
    return t('ward:patient.responsibleNotDocumented')
  }

  return `${given} ${family}`.trim()
}
